import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRole } from "@/contexts/RoleContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea"; // kept for excerpt
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Pencil, Trash2, Star, Search, Filter, Sparkles, Globe, Eye, EyeOff, Loader2, Upload, X, Newspaper } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import { toast } from "sonner";
import {
  useAdminNewsArticles,
  useUpsertNewsArticle,
  useDeleteNewsArticle,
  type NewsArticle,
} from "@/hooks/useNewsArticles";
import { supabase } from "@/integrations/supabase/client";
import { useNewsCategories } from "@/hooks/useNewsCategories";

interface FormData {
  title: string;
  excerpt: string;
  content: string;
  category_id: string;
  source: string;
  published_at: string;
  image_url: string;
  featured: boolean;
  published: boolean;
  generated_by_ai: boolean;
  edited: boolean;
}

const emptyForm: FormData = {
  title: "",
  excerpt: "",
  content: "",
  category_id: "",
  source: "",
  published_at: new Date().toISOString().split("T")[0],
  image_url: "",
  featured: false,
  published: false,
  generated_by_ai: false,
  edited: false,
};

function toForm(a: NewsArticle): FormData {
  return {
    title: a.title,
    excerpt: a.excerpt ?? "",
    content: a.content ?? "",
    category_id: (a as any).category_id ?? "",
    source: a.source ?? "",
    published_at: a.published_at?.split("T")[0] ?? new Date().toISOString().split("T")[0],
    image_url: a.image_url ?? "",
    featured: a.featured ?? false,
    published: a.published ?? false,
    generated_by_ai: a.generated_by_ai ?? false,
    edited: a.edited ?? false,
  };
}

export default function TrendsManagementPage() {
  const { currentRole } = useRole();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: articles = [], isLoading } = useAdminNewsArticles();
  const { data: categorias = [] } = useNewsCategories();
  const upsert = useUpsertNewsArticle();
  const deleteMut = useDeleteNewsArticle();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("all");
  const [filterEstado, setFilterEstado] = useState("all");
  const [uploading, setUploading] = useState(false);

  // Open edit dialog when ?edit=<id> is present (e.g. after generating article from web trends)
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && articles.length > 0) {
      const article = articles.find((a) => a.id === editId);
      if (article) {
        setEditingId(article.id);
        setFormData(toForm(article));
        setIsDialogOpen(true);
        searchParams.delete("edit");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, articles, setSearchParams]);

  if (currentRole !== "super_admin" && currentRole !== "admin") {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold text-destructive">Acceso Denegado</h1>
        <p className="text-muted-foreground mt-2">Solo los administradores pueden acceder a esta página.</p>
      </div>
    );
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se permiten archivos de imagen");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen no debe superar 5MB");
      return;
    }
    setUploading(true);
    const ext = file.name.split(".").pop() ?? "jpg";
    const path = `news/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("content").upload(path, file);
    if (error) {
      toast.error("Error al subir imagen");
      setUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from("content").getPublicUrl(path);
    setFormData((prev) => ({ ...prev, image_url: urlData.publicUrl }));
    setUploading(false);
    toast.success("Imagen subida correctamente");
  };

  const handleOpenDialog = (article?: NewsArticle) => {
    if (article) {
      setEditingId(article.id);
      setFormData(toForm(article));
    } else {
      setEditingId(null);
      setFormData(emptyForm);
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleSave = () => {
    if (!formData.title || !formData.excerpt) {
      toast.error("El título y la descripción son obligatorios");
      return;
    }

    upsert.mutate(
      {
        ...(editingId ? { id: editingId } : {}),
        title: formData.title,
        excerpt: formData.excerpt,
        content: formData.content || null,
        category_id: formData.category_id || null,
        source: formData.source || null,
        published_at: formData.published_at || null,
        image_url: formData.image_url || null,
        featured: formData.featured,
        published: formData.published,
        generated_by_ai: formData.generated_by_ai,
        edited: editingId ? true : formData.edited,
      },
      {
        onSuccess: () => {
          toast.success(editingId ? "Noticia actualizada" : "Noticia creada");
          handleCloseDialog();
        },
        onError: () => toast.error("Error al guardar la noticia"),
      }
    );
  };

  const handleDelete = (id: string) => {
    deleteMut.mutate(id, {
      onSuccess: () => toast.success("Noticia eliminada"),
      onError: () => toast.error("Error al eliminar"),
    });
  };

  const handleToggle = (article: NewsArticle, field: "featured" | "published") => {
    const newVal = !(article[field] ?? false);
    upsert.mutate(
      { id: article.id, title: article.title, [field]: newVal },
      {
        onSuccess: () =>
          toast.success(
            field === "featured"
              ? newVal ? "Marcada como destacada" : "Removida de destacadas"
              : newVal ? "Publicada" : "Despublicada"
          ),
      }
    );
  };

  const filtered = articles.filter((n) => {
    const matchesSearch =
      n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (n.excerpt ?? "").toLowerCase().includes(searchTerm.toLowerCase());
    const cat = categorias.find(c => c.id === (n as any).category_id);
    const matchesCat = filterCategoria === "all" || cat?.name === filterCategoria;
    const matchesState =
      filterEstado === "all" ||
      (filterEstado === "publicada" && n.published) ||
      (filterEstado === "borrador" && !n.published) ||
      (filterEstado === "destacada" && n.featured) ||
      (filterEstado === "ia" && n.generated_by_ai);
    return matchesSearch && matchesCat && matchesState;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Gestión de Tendencias IA</h1>
          <p className="text-muted-foreground mt-1">
            Administra las noticias y tendencias de IA para los participantes
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/trends-management/web")}>
            <Globe className="mr-2 h-4 w-4" />
            Tendencias de la Web
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="mr-2 h-4 w-4" />
                Nueva Noticia
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? "Editar Noticia" : "Nueva Noticia"}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="titulo">Título *</Label>
                <Input
                  id="titulo"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Título de la noticia"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="descripcion">Descripción corta *</Label>
                <Textarea
                  id="descripcion"
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  placeholder="Breve descripción"
                  rows={2}
                />
              </div>
              <div className="grid gap-2">
                <Label>Contenido completo</Label>
                <RichTextEditor
                  value={formData.content}
                  onChange={(html) => setFormData({ ...formData, content: html })}
                  placeholder="Contenido detallado..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Categoría</Label>
                   <Select value={formData.category_id} onValueChange={(v) => {
                    setFormData({ ...formData, category_id: v });
                  }}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      {categorias.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="fuente">Fuente</Label>
                  <Input
                    id="fuente"
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    placeholder="Ej: OpenAI Blog"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="fecha">Fecha de publicación</Label>
                  <Input
                    id="fecha"
                    type="date"
                    value={formData.published_at}
                    onChange={(e) => setFormData({ ...formData, published_at: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Imagen</Label>
                  {formData.image_url ? (
                    <div className="relative w-full rounded-md border overflow-hidden">
                      <img src={formData.image_url} alt="Preview" className="w-full h-40 object-cover" />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_url: "" })}
                        className="absolute top-2 right-2 rounded-full bg-destructive text-destructive-foreground p-1 shadow-md"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">Sin imagen asignada</p>
                  )}
                  <div className="flex gap-2">
                    <label className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-center gap-2 rounded-md border border-dashed border-input bg-background px-3 py-2 text-sm text-muted-foreground hover:bg-accent transition-colors">
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        {uploading ? "Subiendo..." : formData.image_url ? "Cambiar imagen" : "Subir imagen"}
                      </div>
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
                    </label>
                  </div>
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="O pegar URL de imagen..."
                    className="text-xs"
                  />
                </div>
              </div>
              <div className="flex items-center gap-6 pt-2">
                <div className="flex items-center space-x-2">
                  <Switch id="destacada" checked={formData.featured} onCheckedChange={(c) => setFormData({ ...formData, featured: c })} />
                  <Label htmlFor="destacada">Destacada</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch id="publicada" checked={formData.published} onCheckedChange={(c) => setFormData({ ...formData, published: c })} />
                  <Label htmlFor="publicada">Publicar</Label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={handleCloseDialog}>Cancelar</Button>
                <Button onClick={handleSave} disabled={upsert.isPending}>
                  {upsert.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {editingId ? "Guardar Cambios" : "Crear Noticia"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold">{articles.length}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Destacadas</CardTitle>
            <Star className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold">{articles.filter((n) => n.featured).length}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Publicadas</CardTitle>
            <Eye className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold">{articles.filter((n) => n.published).length}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Generadas IA</CardTitle>
            <Sparkles className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent><div className="text-2xl font-bold">{articles.filter((n) => n.generated_by_ai).length}</div></CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Buscar noticias..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <Select value={filterCategoria} onValueChange={setFilterCategoria}>
          <SelectTrigger className="w-[200px]">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {categorias.map((cat) => (
              <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterEstado} onValueChange={setFilterEstado}>
          <SelectTrigger className="w-[180px]">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="publicada">Publicadas</SelectItem>
            <SelectItem value="borrador">Borradores</SelectItem>
            <SelectItem value="destacada">Destacadas</SelectItem>
            <SelectItem value="ia">Generadas IA</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">Dest.</TableHead>
                <TableHead>Título</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Fuente</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <button onClick={() => handleToggle(item, "featured")} className="hover:scale-110 transition-transform">
                      <Star className={`h-5 w-5 ${item.featured ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[300px]">
                      <p className="font-medium truncate">{item.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{item.excerpt}</p>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline">{categorias.find(c => c.id === (item as any).category_id)?.name ?? '—'}</Badge></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{item.source ?? item.author}</TableCell>
                  <TableCell className="text-sm">{item.published_at?.split("T")[0]}</TableCell>
                  <TableCell>
                    <button onClick={() => handleToggle(item, "published")}>
                      {item.published ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-200">
                          <Eye className="mr-1 h-3 w-3" /> Publicada
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="hover:bg-muted">
                          <EyeOff className="mr-1 h-3 w-3" /> Borrador
                        </Badge>
                      )}
                    </button>
                  </TableCell>
                  <TableCell>
                    {item.generated_by_ai ? (
                      <div className="flex items-center gap-1">
                        <Badge className="bg-purple-100 text-purple-800"><Sparkles className="mr-1 h-3 w-3" /> IA</Badge>
                        {item.edited && <Badge variant="outline" className="text-xs">Editada</Badge>}
                      </div>
                    ) : (
                      <Badge variant="secondary">Manual</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(item)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="text-destructive hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No se encontraron noticias
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
